import { createClient } from '@/lib/supabase/client';
import type { Diamond, DiamondStatus } from '@/lib/database';
import { newId, orThrow, DEFAULT_PAGE_SIZE } from './useEntityList';

// DB columns match the UI type 1:1; only null-coercion is needed here.
type DiamondRow = Omit<
  Diamond,
  | 'cut'
  | 'polish'
  | 'symmetry'
  | 'fluorescence'
  | 'measurements'
  | 'location'
  | 'cert_body'
  | 'cert_number'
  | 'notes'
> & {
  cut: string | null;
  polish: string | null;
  symmetry: string | null;
  fluorescence: string | null;
  measurements: string | null;
  location: string | null;
  cert_body: string | null;
  cert_number: string | null;
  notes: string | null;
};

function toDiamond(row: DiamondRow): Diamond {
  return {
    ...row,
    cut: row.cut ?? '',
    polish: row.polish ?? '',
    symmetry: row.symmetry ?? '',
    fluorescence: row.fluorescence ?? '',
    measurements: row.measurements ?? '',
    location: row.location ?? '',
    cert_body: row.cert_body ?? '',
    cert_number: row.cert_number ?? '',
    notes: row.notes ?? '',
  };
}

export async function listDiamonds(): Promise<Diamond[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('diamonds')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .order('created_at', { ascending: false });
  return (orThrow(data, error) as DiamondRow[]).map(toDiamond);
}

export async function getDiamond(id: string): Promise<Diamond | null> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('diamonds')
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .eq('id', id)
    .maybeSingle();
  return data ? toDiamond(data as DiamondRow) : (orThrow(data, error), null);
}

export async function createDiamond(
  input: Omit<Diamond, 'id' | 'created_at' | 'updated_at'>
): Promise<Diamond> {
  const supabase = createClient();
  const now = new Date().toISOString();
  const { data, error } = await supabase
    .from('diamonds')
    .insert({ ...input, id: newId('dia'), created_at: now, updated_at: now })
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toDiamond(orThrow(data, error) as DiamondRow);
}

export async function updateDiamond(
  id: string,
  patch: Partial<Omit<Diamond, 'id' | 'created_at'>>
): Promise<Diamond> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('diamonds')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*')
    .limit(DEFAULT_PAGE_SIZE)
    .single();
  return toDiamond(orThrow(data, error) as DiamondRow);
}

export async function deleteDiamond(id: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('diamonds').delete().eq('id', id);
  orThrow(null, error);
}

/** Status transitions used by memo/hold/sale workflows. Non-throwing wrapper kept thin. */
export async function setDiamondStatus(id: string, status: DiamondStatus): Promise<void> {
  await updateDiamond(id, { status });
}
